/**
 * FirstRipRotationTicker
 *
 * A thin, dismissable scarcity ticker shown at the top of the homepage when
 * a First Rip series has recently sold out and rotated to a new series.
 * Powered by the PUBLIC endpoint `/api/rewards/first-rip/latest-rotation`
 * so it works for BOTH signed-in and signed-out visitors (top-of-funnel
 * scarcity proof).
 *
 * Behaviour:
 *  - Fetches once on mount; if no recent rotation (last 7 days), renders null.
 *  - Persists per-event dismissal in localStorage so a closed ticker stays
 *    closed for that visitor until the NEXT rotation event fires.
 *  - On mobile (<md) the message auto-scrolls (marquee) so the full copy +
 *    "claim yours →" CTA stays readable on narrow viewports. On md+ it
 *    renders as a static centered banner with no animation.
 *  - All network/parse errors are swallowed (returns null) — this widget
 *    must never break the rest of the homepage.
 *  - Self-contained: no auth dependency, no global state, no portal.
 *  - Respects `prefers-reduced-motion`: marquee falls back to a static
 *    truncated line for users who have motion disabled.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import axios from "axios";
import { API } from "../../utils";

const DISMISS_KEY_PREFIX = "blk_rotation_ticker_dismissed:";

const FirstRipRotationTicker = () => {
  const [event, setEvent] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/rewards/first-rip/latest-rotation`, {
          // Public endpoint — explicitly suppress auth interceptors that might
          // try to attach a stale token for signed-out visitors.
          withCredentials: false,
          timeout: 5000,
        });
        if (cancelled) return;
        const ev = res?.data?.event;
        if (!ev || !ev.id) return;
        // Per-event dismissal check
        try {
          if (localStorage.getItem(DISMISS_KEY_PREFIX + ev.id) === "1") {
            return;
          }
        } catch {
          /* localStorage unavailable (Safari private mode) — show anyway */
        }
        setEvent(ev);
      } catch {
        /* swallow — ticker is non-critical */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDismiss = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    if (event?.id) {
      try {
        localStorage.setItem(DISMISS_KEY_PREFIX + event.id, "1");
      } catch {
        /* ignore */
      }
    }
    setDismissed(true);
  };

  if (!event || dismissed) return null;

  const closedName = event.closed_drop_name || "Founder Edition";
  const nextName = event.next_drop_name;
  const showNext = Boolean(event.next_drop_active && nextName);
  const cap = event.max_real_claims
    ? event.max_real_claims.toLocaleString()
    : "limited";

  // Marquee content (mobile only). Repeated twice in the DOM so the second
  // copy slides in seamlessly when the first scrolls off — gives the look of
  // an infinite ticker without JS measurement work.
  const MarqueeMessage = () => (
    <span className="inline-flex items-center gap-2 whitespace-nowrap text-xs text-orange-100/95">
      <span className="font-semibold tracking-wide uppercase text-orange-300">
        Just Sold Out
      </span>
      <span className="text-orange-50/90">
        {closedName} — all {cap} Founder serials claimed.
      </span>
      {showNext && (
        <span className="text-amber-200">
          Next series{" "}
          <span className="font-semibold">'{nextName}'</span> is LIVE —{" "}
          <span className="underline decoration-orange-400/60 underline-offset-2">
            claim yours →
          </span>
        </span>
      )}
      {/* visual gap between marquee loops */}
      <span aria-hidden className="inline-block w-12" />
    </span>
  );

  return (
    <div
      data-testid="first-rip-rotation-ticker"
      className="relative w-full overflow-hidden border-b border-orange-500/30 bg-gradient-to-r from-orange-950/80 via-amber-900/70 to-orange-950/80 backdrop-blur-sm"
    >
      {/* Local keyframes — scoped so the animation never leaks. */}
      <style>{`
        @keyframes blkRotationMarquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .blk-rotation-marquee-track { animation: none !important; }
        }
      `}</style>

      <Link
        to="/drops"
        data-testid="first-rip-rotation-ticker-link"
        className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 max-w-7xl mx-auto group"
      >
        <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-orange-500/20 border border-orange-400/40">
          <Sparkles className="w-3.5 h-3.5 text-orange-300" />
        </span>

        {/* MOBILE (<md): marquee */}
        <div className="md:hidden flex-1 min-w-0 overflow-hidden relative">
          <div
            className="blk-rotation-marquee-track flex w-max"
            style={{ animation: "blkRotationMarquee 22s linear infinite" }}
            data-testid="first-rip-rotation-ticker-marquee"
          >
            <MarqueeMessage />
            <MarqueeMessage />
          </div>
          {/* Edge fades so text doesn't visually slam against the icon / X */}
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-orange-950/95 to-transparent"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-orange-950/95 to-transparent"
          />
        </div>

        {/* TABLET / DESKTOP (md+): static single line */}
        <div className="hidden md:block flex-1 min-w-0 text-sm text-orange-100/95">
          <span className="font-semibold tracking-wide uppercase text-orange-300 mr-2">
            Just Sold Out
          </span>
          <span className="text-orange-50/90">
            {closedName} — all {cap} Founder serials claimed.
          </span>
          {showNext && (
            <span className="ml-2 text-amber-200">
              Next series <span className="font-semibold">'{nextName}'</span> is LIVE —
              <span className="ml-1 underline decoration-orange-400/60 underline-offset-2 group-hover:text-white transition-colors">
                claim yours →
              </span>
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          data-testid="first-rip-rotation-ticker-dismiss"
          aria-label="Dismiss sold-out notice"
          className="flex-shrink-0 p-1.5 rounded-full text-orange-300/70 hover:text-orange-100 hover:bg-orange-500/15 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </Link>
    </div>
  );
};

export default FirstRipRotationTicker;
